import { EmployeeLeaveStatusEnum } from '@electricjs/core_entity-client';
import { EmployeeLeave } from '@/types/employees';
import { ISO_DATE_FORMAT } from '@/constants/common';
import formatDate from './formatDate';

/**
 * Checks whether an employee has a currently in-progress leave, i.e. whether
 * they can't be newly assigned as a champion/assignee (mirrors the backend's
 * `EmployeeLeavesService.assert_employee_not_on_leave`, which only blocks on
 * `IN_PROGRESS` leaves - a `SCHEDULED` leave doesn't take effect yet).
 *
 * @param leaves - The employee's leaves, or undefined if not yet fetched.
 * @returns {boolean} True if the employee is currently on leave.
 */
export const isEmployeeOnLeave = (leaves: EmployeeLeave[] | undefined) => {
  return Boolean(
    leaves?.some(leave => leave.status === EmployeeLeaveStatusEnum.InProgress)
  );
};

const isActiveLeaveStatus = (status: EmployeeLeaveStatusEnum) =>
  status === EmployeeLeaveStatusEnum.InProgress ||
  status === EmployeeLeaveStatusEnum.Scheduled;

/**
 * Returns the in-progress/scheduled leaves (of the given leaves) that
 * overlap the given date window (mirrors the backend's
 * `EmployeeLeave.overlaps`). Used both to decide whether to block selection
 * (`isEmployeeOnLeaveDuring`) and to annotate only the leaves actually
 * relevant to that window, rather than any leave the employee happens to have.
 *
 * A window that ends exactly when the other starts (or vice versa) doesn't
 * count as overlapping.
 *
 * @param leaves - The employee's leaves, or undefined if not yet fetched.
 * @param startDate - The start date of the other window.
 * @param returnDate - The end date of the other window, or null/undefined if open-ended.
 * @returns The leaves that overlap the given window.
 */
export const getOverlappingLeaves = (
  leaves: EmployeeLeave[] | undefined,
  startDate: Date,
  returnDate: Date | null | undefined
): EmployeeLeave[] => {
  // Compared as `yyyy-MM-dd` strings - the same shape the API sends leave
  // dates in - rather than as `Date` objects, since `startDate`/`returnDate`
  // here come from a date picker (local midnight) while `leave.startDate`/
  // `leave.returnDate` get parsed as UTC midnight; comparing the two as
  // `Date` instants can shift the boundary by the local UTC offset.
  const windowStart = formatDate(startDate, ISO_DATE_FORMAT);
  const windowReturn = returnDate
    ? formatDate(returnDate, ISO_DATE_FORMAT)
    : null;

  return (leaves ?? []).filter(leave => {
    if (!isActiveLeaveStatus(leave.status)) {
      return false;
    }

    // The API sends `null` (not an omitted field) for an open-ended leave,
    // so this must be a truthy check, not `!== undefined` - the latter lets
    // `null` through, making an open-ended leave look like it "ended".
    const thisEndsBeforeOtherStarts =
      Boolean(leave.returnDate) && leave.returnDate! <= windowStart;
    const otherEndsBeforeThisStarts =
      Boolean(windowReturn) && windowReturn! <= leave.startDate;

    return !(thisEndsBeforeOtherStarts || otherEndsBeforeThisStarts);
  });
};

/**
 * Checks whether an employee has an in-progress or scheduled leave that
 * overlaps the given date window. Unlike `isEmployeeOnLeave`, this also
 * blocks on `SCHEDULED` leave, since the given window is the leave being
 * newly created or edited, not "today".
 *
 * @param leaves - The employee's leaves, or undefined if not yet fetched.
 * @param startDate - The start date of the other window.
 * @param returnDate - The end date of the other window, or null/undefined if open-ended.
 * @returns {boolean} True if any leave overlaps the given window.
 */
export const isEmployeeOnLeaveDuring = (
  leaves: EmployeeLeave[] | undefined,
  startDate: Date,
  returnDate: Date | null | undefined
) => getOverlappingLeaves(leaves, startDate, returnDate).length > 0;
